/**
 * Canonical UI release metadata — injected at deploy time into index.html.
 * Never hard-code release values in React components.
 * Never invent a git SHA from the current checkout / main branch.
 */

const PLACEHOLDER_RE = /^%[A-Z0-9_]+%$/;
const MALFORMED_HINTS = /^(undefined|null|unknown|local|localhost)$/i;

function readRaw(value) {
  if (value == null) return "";
  const trimmed = String(value).trim();
  if (!trimmed || PLACEHOLDER_RE.test(trimmed)) return "";
  if (MALFORMED_HINTS.test(trimmed)) return "";
  return trimmed;
}

function normalizeEnvironment(raw) {
  const v = String(raw || "").toLowerCase();
  if (v === "staging" || v === "production" || v === "development") return v;
  const fromEnv = String(process.env.REACT_APP_ENV || "").toLowerCase();
  if (fromEnv === "staging" || fromEnv === "production" || fromEnv === "development") {
    return fromEnv;
  }
  return "unknown";
}

export function getReleaseMetadata(win = typeof window !== "undefined" ? window : {}) {
  const releaseId =
    readRaw(win.__KK_RELEASE_ID) ||
    (readRaw(win.__BUILD_VERSION) ? `v2.${readRaw(win.__BUILD_VERSION)}` : "");
  const gitSha = readRaw(win.__KK_GIT_SHA);
  const buildTimestamp =
    readRaw(win.__KK_BUILD_TIMESTAMP) || readRaw(win.__BUILD_TIMESTAMP);
  const environment = normalizeEnvironment(readRaw(win.__KK_ENVIRONMENT));
  const isPlaceholder = !releaseId || !gitSha || !buildTimestamp;

  if (isPlaceholder) {
    return {
      releaseId: releaseId || "v2.uninjected (localhost)",
      gitSha: gitSha || "uninjected",
      buildTimestamp: buildTimestamp || "",
      environment:
        environment === "unknown"
          ? normalizeEnvironment(process.env.REACT_APP_ENV || "development")
          : environment,
      isPlaceholder: true,
    };
  }

  return {
    releaseId: releaseId.startsWith("v") ? releaseId : `v${releaseId}`,
    gitSha,
    buildTimestamp,
    environment,
    isPlaceholder: false,
  };
}

export function assertEnvironmentMatchesBuild(meta, expected) {
  if (meta.isPlaceholder) return expected === "development" || expected === "unknown";
  return meta.environment === expected;
}

export function assertProductionExcludesStaging(meta) {
  if (meta.environment !== "production" || meta.isPlaceholder) return true;
  const blob = `${meta.releaseId} ${meta.gitSha} ${meta.buildTimestamp} ${meta.environment}`.toLowerCase();
  return !blob.includes("staging");
}
